import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from "@/components/ui/use-toast";
import { api } from '@/api';
import { useAuth } from '@/contexts/AuthContext';

export function NodePropsForm({ node, onChange, sections, currentPageId, isNewsletterEditor }) {
  const { t } = useTranslation();
  const { token } = useAuth?.() || {};
  const [assetPicker, setAssetPicker] = React.useState({ open: false, fieldName: null, kind: 'all' });
  if(!node) return <div className='p-3 text-[11px] text-slate-500'>Kein Node ausgewählt</div>;
  const fields = getFields(node.type);
  const showProps = sections?.props !== false;
  const showStyle = sections?.style !== false;
  const showAdvanced = sections?.advanced === true;
  const groups = React.useMemo(()=> groupFields(fields), [fields]);

  const uploadToField = React.useCallback(async(file, fieldName)=>{
    if(!token) return;
    try{
      if (isNewsletterEditor) {
        const res = await api.uploadNewsletterAsset(token, file);
        if(res?.url) onChange({ [fieldName]: res.url });
      } else {
        const pageId = currentPageId || node.pageId || node.rootPageId || node._pageId || '';
        const res = await api.uploadPageAsset(token, pageId, file);
        if(res?.url) onChange({ [fieldName]: res.url });
      }
    }catch(e){
      toast({ title: e.message || t('common.error'), variant: 'destructive' });
    }
  }, [token, isNewsletterEditor, currentPageId, node?.pageId, node?.rootPageId, node?._pageId, onChange, t]);

  const withDrop = (fieldName) => ({
    onDragOver: (e)=>{ e.preventDefault(); },
    onDragEnter: (e)=>{ e.preventDefault(); e.currentTarget?.classList?.add('ring-2','ring-sky-400','ring-offset-2','ring-offset-white'); },
    onDragLeave: (e)=>{ e.preventDefault(); e.currentTarget?.classList?.remove('ring-2','ring-sky-400','ring-offset-2','ring-offset-white'); },
    onDrop: async(e)=>{
      e.preventDefault();
      e.currentTarget?.classList?.remove('ring-2','ring-sky-400','ring-offset-2','ring-offset-white');
      const f = e.dataTransfer?.files && e.dataTransfer.files[0];
      if(f && token) await uploadToField(f, fieldName);
    }
  });

  return (
    <div className='p-3 space-y-3 text-[11px] bg-background text-slate-900 min-h-full'>
      {showProps && (
        <div className="space-y-2">
          <div className='text-[10px] font-semibold tracking-wide uppercase text-slate-500 mb-1'>Inhalt</div>
          <div className='space-y-2'>
            {groups.map((g, idx) => (
              <AccordionSection key={g.title || idx} title={g.title || 'Gruppe'} defaultOpen={g.defaultOpen}>
                <div className='space-y-2'>
                  {g.items.map(f => (
                    <div key={f.name} className='space-y-1'>
                      <label className='block text-[10px] text-slate-600'>{f.label}</label>
                      {f.type === 'text' && (
                        <div className='flex items-center gap-2'>
                          <input className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900 placeholder:text-slate-400' value={node.props?.[f.name] || ''} onChange={e=>onChange({ [f.name]: e.target.value })} />
                        </div>
                      )}
                      {f.type === 'image' && (
                        <div className='flex items-center gap-2 rounded' {...withDrop(f.name)} title={token ? 'Datei hierher ziehen zum Upload' : undefined}>
                          <input className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900 placeholder:text-slate-400' value={node.props?.[f.name] || ''} onChange={e=>onChange({ [f.name]: e.target.value })} placeholder='https://... oder /files/...' />
                          {token && (
                            <>
                              <UploadButton label='Upload' onSelect={(file)=> uploadToField(file, f.name)} accept='.png,.jpg,.jpeg,.gif,.webp' />
                              <button
                                type='button'
                                className='text-[10px] px-2 py-1 border rounded bg-background hover:bg-slate-50'
                                onClick={(e)=>{
                                  e.preventDefault();
                                  setAssetPicker({ open: true, fieldName: f.name, kind: 'image' });
                                }}
                                title='Bibliothek öffnen'
                              >Bibliothek</button>
                            </>
                          )}
                        </div>
                      )}
                      {f.type === 'video' && (
                        <div className='flex items-center gap-2 rounded' {...withDrop(f.name)} title={token ? 'Datei hierher ziehen zum Upload' : undefined}>
                          <input className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900 placeholder:text-slate-400' value={node.props?.[f.name] || ''} onChange={e=>onChange({ [f.name]: e.target.value })} placeholder='https://... oder /files/...' />
                          {token && (
                            <>
                              <UploadButton label='Upload' onSelect={(file)=> uploadToField(file, f.name)} accept='.mp4' />
                              <button
                                type='button'
                                className='text-[10px] px-2 py-1 border rounded bg-background hover:bg-slate-50'
                                onClick={(e)=>{
                                  e.preventDefault();
                                  setAssetPicker({ open: true, fieldName: f.name, kind: 'video' });
                                }}
                                title='Bibliothek öffnen'
                              >Bibliothek</button>
                            </>
                          )}
                        </div>
                      )}
                      {f.type === 'media' && (
                        <div className='flex items-center gap-2 rounded' {...withDrop(f.name)} title={token ? 'Datei hierher ziehen zum Upload' : undefined}>
                          <input className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900 placeholder:text-slate-400' value={node.props?.[f.name] || ''} onChange={e=>onChange({ [f.name]: e.target.value })} placeholder='https://... oder /files/...' />
                          {token && (
                            <>
                              <UploadButton label='Upload' onSelect={(file)=> uploadToField(file, f.name)} accept='.png,.jpg,.jpeg,.gif,.webp,.mp4' />
                              <button
                                type='button'
                                className='text-[10px] px-2 py-1 border rounded bg-background hover:bg-slate-50'
                                onClick={(e)=>{
                                  e.preventDefault();
                                  setAssetPicker({ open: true, fieldName: f.name, kind: 'all' });
                                }}
                                title='Bibliothek öffnen'
                              >Bibliothek</button>
                            </>
                          )}
                        </div>
                      )}
                      {f.type === 'textarea' && (
                        <textarea className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] h-24 text-slate-900 placeholder:text-slate-400' value={node.props?.[f.name] || ''} onChange={e=>onChange({ [f.name]: e.target.value })} />
                      )}
                      {f.type === 'select' && (()=>{
                        const raw = node.props?.[f.name];
                        let valueStr = raw;
                        if (typeof raw === 'boolean') {
                          const hasZero = Array.isArray(f.options) && f.options.some(o => String(o.value) === '0');
                          const hasOne = Array.isArray(f.options) && f.options.some(o => String(o.value) === '1');
                          if (hasZero) {
                            // Scheme: '' = yes/default, '0' = no
                            valueStr = raw ? '' : '0';
                          } else if (hasOne) {
                            // Scheme: '' = no, '1' = yes
                            valueStr = raw ? '1' : '';
                          } else {
                            valueStr = raw ? '1' : '';
                          }
                        }
                        if (valueStr === undefined || valueStr === null) valueStr = '';
                        valueStr = String(valueStr);
                        return (
                          <select className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={valueStr} onChange={e=>onChange({ [f.name]: e.target.value })}>
                            {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        );
                      })()}
                      {f.type === 'number' && (
                        <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.[f.name] ?? ''} onChange={e=>onChange({ [f.name]: e.target.value === '' ? undefined : Number(e.target.value) })} />
                      )}
                      {f.type === 'datetime-local' && (
                        <input type='datetime-local' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.[f.name] || ''} onChange={e=>onChange({ [f.name]: e.target.value })} />
                      )}
                      {f.type === 'color' && (
                        <ColorInput value={node.props?.[f.name] || ''} onChange={(val)=>onChange({ [f.name]: val })} />
                      )}
                      {f.type === 'richtext' && (
                        <RichTextEditor value={node.props?.[f.name] || ''} onChange={(html)=> onChange({ [f.name]: html })} isNewsletterEditor={isNewsletterEditor} />
                      )}
                      {f.type === 'optionsList' && (
                        <OptionsListEditor value={node.props?.[f.name] || '[]'} onChange={(val)=>onChange({ [f.name]: val })} />
                      )}
                    </div>
                  ))}
                </div>
              </AccordionSection>
            ))}
          </div>
        </div>
      )}

      {showStyle && (
        <div className="space-y-2">
          <div className='text-[10px] font-semibold tracking-wide uppercase text-slate-500 mb-1'>Stil</div>
          <div className='space-y-2'>
            {/* Typografie */}
            <div className='border border-slate-200 bg-slate-50 rounded-lg p-2 space-y-2'>
              <div className='text-[10px] font-medium text-slate-700 uppercase tracking-wide'>Typografie</div>
              <div className='space-y-1'>
                <label className='block text-[10px] text-slate-600'>Textfarbe</label>
                <ColorInput value={node.props?.color || ''} onChange={(val)=>onChange({ color: val })} />
              </div>
              <div className='space-y-1'>
                <label className='block text-[10px] text-slate-600'>Text Ausrichtung</label>
                <select className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.textAlign || ''} onChange={e=>onChange({ textAlign: e.target.value })}>
                  <option value=''>default</option>
                  <option value='left'>left</option>
                  <option value='center'>center</option>
                  <option value='right'>right</option>
                </select>
              </div>
              <div className='space-y-1'>
                <label className='block text-[10px] text-slate-600'>Font Size (px)</label>
                <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={typeof node.props?.fontSize === 'number' ? node.props.fontSize : ''} onChange={e=>onChange({ fontSize: e.target.value ? Number(e.target.value) : undefined })} placeholder='z.B. 16' />
              </div>
            </div>

            {/* Layout */}
            <div className='border border-slate-200 bg-slate-50 rounded-lg p-2 space-y-2'>
              <div className='text-[10px] font-medium text-slate-700 uppercase tracking-wide'>Layout</div>
              <div className='space-y-1'>
                <label className='block text-[10px] text-slate-600'>Padding</label>
                <select className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.padding || ''} onChange={e=>onChange({ padding: e.target.value })}>
                  <option value=''>none</option>
                  <option value='sm'>sm</option>
                  <option value='md'>md</option>
                  <option value='lg'>lg</option>
                  <option value='xl'>xl</option>
                </select>
              </div>
              <div className='space-y-1'>
                <label className='block text-[10px] text-slate-600'>Max Width (px)</label>
                <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.maxWidthPx ?? ''} onChange={e=>onChange({ maxWidthPx: e.target.value ? Number(e.target.value) : undefined })} />
              </div>
            </div>

            {/* Space */}
            <div className='border border-slate-200 bg-slate-50 rounded-lg p-2 space-y-2'>
              <div className='text-[10px] font-medium text-slate-700 uppercase tracking-wide'>Space</div>
              <div className='bg-background rounded-md border border-slate-200/70 px-2 py-2 text-[9px] text-slate-500 relative overflow-hidden'>
                {/* Margin top */}
                <div className='absolute left-1/2 -translate-x-1/2 -top-2 bg-slate-50 px-1 rounded border border-slate-200 text-[8px] uppercase tracking-wide whitespace-nowrap'>
                  Margin
                </div>
                <div className='flex flex-col items-stretch gap-1'>
                  <div className='flex items-center justify-center'>
                    <input
                      type='number'
                      className='w-9 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[10px] bg-slate-50 shrink-0'
                      value={node.props?.marginTopPx ?? ''}
                      onChange={e=>onChange({ marginTopPx: e.target.value ? Number(e.target.value) : undefined })}
                    />
                  </div>
                  <div className='flex items-stretch gap-0.5'>
                    {/* Margin left */}
                    <div className='flex flex-col justify-center items-center shrink-0'>
                      <input
                        type='number'
                        className='w-9 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[10px] bg-slate-50 shrink-0'
                        value={node.props?.marginLeftPx ?? ''}
                        onChange={e=>onChange({ marginLeftPx: e.target.value ? Number(e.target.value) : undefined })}
                      />
                    </div>
                    {/* Padding box */}
                    <div className='flex-1 min-w-0 bg-slate-50/80 rounded border border-slate-200 px-1.5 py-1.5 relative mt-2'>
                      <div className='absolute left-1/2 -translate-x-1/2 -top-2 bg-background px-1 rounded border border-slate-200 text-[8px] uppercase tracking-wide whitespace-nowrap'>
                        Padding
                      </div>
                      <div className='flex flex-col items-stretch gap-0.5'>
                        <div className='flex items-center justify-center'>
                          <input
                            type='number'
                            className='w-8 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[9px] bg-background shrink-0'
                            value={node.props?.paddingTopPx ?? ''}
                            onChange={e=>onChange({ paddingTopPx: e.target.value ? Number(e.target.value) : undefined })}
                          />
                        </div>
                        <div className='flex items-stretch gap-0.5'>
                          <input
                            type='number'
                            className='w-8 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[9px] bg-background shrink-0'
                            value={node.props?.paddingLeftPx ?? ''}
                            onChange={e=>onChange({ paddingLeftPx: e.target.value ? Number(e.target.value) : undefined })}
                          />
                          <div className='flex-1 min-w-0 flex items-center justify-center text-[8px] text-slate-400'>
                            cont
                          </div>
                          <input
                            type='number'
                            className='w-8 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[9px] bg-background shrink-0'
                            value={node.props?.paddingRightPx ?? ''}
                            onChange={e=>onChange({ paddingRightPx: e.target.value ? Number(e.target.value) : undefined })}
                          />
                        </div>
                        <div className='flex items-center justify-center'>
                          <input
                            type='number'
                            className='w-8 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[9px] bg-background shrink-0'
                            value={node.props?.paddingBottomPx ?? ''}
                            onChange={e=>onChange({ paddingBottomPx: e.target.value ? Number(e.target.value) : undefined })}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Margin right */}
                    <div className='flex flex-col justify-center items-center shrink-0'>
                      <input
                        type='number'
                        className='w-9 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[10px] bg-slate-50 shrink-0'
                        value={node.props?.marginRightPx ?? ''}
                        onChange={e=>onChange({ marginRightPx: e.target.value ? Number(e.target.value) : undefined })}
                      />
                    </div>
                  </div>
                  <div className='flex items-center justify-center'>
                    <input
                      type='number'
                      className='w-9 text-center border border-slate-200 rounded px-0.5 py-0.5 text-[10px] bg-slate-50 shrink-0'
                      value={node.props?.marginBottomPx ?? ''}
                      onChange={e=>onChange({ marginBottomPx: e.target.value ? Number(e.target.value) : undefined })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Size */}
            <div className='border border-slate-200 bg-slate-50 rounded-lg p-2 space-y-2'>
              <div className='text-[10px] font-medium text-slate-700 uppercase tracking-wide'>Size</div>
              <div className='grid grid-cols-2 gap-2'>
                <div className='space-y-1'>
                  <label className='block text-[10px] text-slate-600'>Width (px)</label>
                  <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.widthPx ?? ''} onChange={e=>onChange({ widthPx: e.target.value ? Number(e.target.value) : undefined })} />
                </div>
                <div className='space-y-1'>
                  <label className='block text-[10px] text-slate-600'>Height (px)</label>
                  <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.heightPx ?? ''} onChange={e=>onChange({ heightPx: e.target.value ? Number(e.target.value) : undefined })} />
                </div>
                <div className='space-y-1'>
                  <label className='block text-[10px] text-slate-600'>Min width (px)</label>
                  <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.minWidthPx ?? ''} onChange={e=>onChange({ minWidthPx: e.target.value ? Number(e.target.value) : undefined })} />
                </div>
                <div className='space-y-1'>
                  <label className='block text-[10px] text-slate-600'>Max width (px)</label>
                  <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.maxWidthPx ?? ''} onChange={e=>onChange({ maxWidthPx: e.target.value ? Number(e.target.value) : undefined })} />
                </div>
              </div>
              <div className='space-y-1'>
                <label className='block text-[10px] text-slate-600'>Flex grow</label>
                <input type='number' className='border border-slate-200 bg-background rounded w-full px-2 py-1 text-[11px] text-slate-900' value={node.props?.flexGrow ?? ''} onChange={e=>onChange({ flexGrow: e.target.value ? Number(e.target.value) : undefined })} />
              </div>
            </div>

            {/* Hintergrund */}
            <div className='border border-slate-200 bg-slate-50 rounded-lg p-2 space-y-2'>
              <div className='text-[10px] font-medium text-slate-700 uppercase tracking-wide'>Hintergrund</div>
              <div className='space-y-1'>
                <label className='block text-[10px] text-slate-600'>Farbe / Token</label>
                <ColorInput value={node.props?.background || ''} onChange={(val)=>onChange({ background: val })} />
              </div>
            </div>
          </div>
        </div>
      )}

      {showAdvanced && (
        <AdvancedPropsEditor propsObj={node.props || {}} onApply={(obj)=> onChange(obj)} />
      )}
      <AssetLibraryModal
        open={assetPicker.open}
        token={token}
        kind={assetPicker.kind}
        onClose={()=> setAssetPicker({ open: false, fieldName: null, kind: 'all' })}
        onPick={(url)=>{
          if(assetPicker.fieldName){
            onChange({ [assetPicker.fieldName]: url });
          }
          setAssetPicker({ open: false, fieldName: null, kind: 'all' });
        }}
      />
    </div>
  );
}

function AssetLibraryModal({ open, token, kind='all', onClose, onPick }){
  const { t } = useTranslation();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [scope, setScope] = React.useState('all');
  const [query, setQuery] = React.useState('');
  const [folders, setFolders] = React.useState([]);
  const [folderPrefix, setFolderPrefix] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(null);
  const pageSize = 9;
  const [cursorByPage, setCursorByPage] = React.useState({});
  const [nextCursor, setNextCursor] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(()=>{
    if(!open) return;
    let cancelled = false;
    (async()=>{
      if(!token) return;
      setLoading(true);
      try{
        const res = await api.listAssetFolders(token, { scope });
        if(cancelled) return;
        const list = Array.isArray(res?.folders) ? res.folders : [];
        setFolders(list);
        // Choose a sensible default folder
        if(!folderPrefix && list.length){
          setFolderPrefix(list[0].prefix);
          setCursorByPage({});
          setPage(1);
        }
      }catch(e){
        if(!cancelled) toast({ title: e.message || t('common.error'), variant: 'destructive' });
      }finally{
        if(!cancelled) setLoading(false);
      }
    })();
    return ()=>{ cancelled = true; };
  }, [open, token, scope, t]);

  React.useEffect(()=>{
    if(!open) return;
    if(!token) return;
    if(!folderPrefix) return;
    let cancelled = false;
    (async()=>{
      setLoading(true);
      try{
        const cursor = cursorByPage?.[page] || undefined;
        const res = await api.listAssetLibraryPage(token, {
          kind,
          scope,
          prefix: folderPrefix,
          q: query.trim() || undefined,
          page,
          pageSize,
          cursor,
        });
        if(cancelled) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(typeof res?.total === 'number' ? res.total : null);
        setNextCursor(res?.nextCursor ?? null);
        if(res?.nextCursor){
          setCursorByPage(prev => ({ ...prev, [page+1]: res.nextCursor }));
        }
      }catch(e){
        if(!cancelled) toast({ title: e.message || t('common.error'), variant: 'destructive' });
      }finally{
        if(!cancelled) setLoading(false);
      }
    })();
    return ()=>{ cancelled = true; };
  }, [open, token, kind, scope, folderPrefix, query, page, pageSize, refreshKey, t]);

  React.useEffect(()=>{
    if(!open) return;
    const onKey = (e)=>{ if(e.key==='Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return ()=> window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if(!open) return null;

  const isVideo = (urlOrKey) => String(urlOrKey||'').toLowerCase().includes('.mp4');
  const scopeLabel = (s) => s === 'page' ? 'Pages' : s === 'newsletter' ? 'Newsletter' : 'Alle';

  const canPrev = page > 1;
  const canNext = (()=>{
    if (typeof total === 'number') return page * pageSize < total;
    return Boolean(nextCursor);
  })();

  const folderLabel = (f) => {
    return f?.label || f?.prefix || '';
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="bg-background rounded-lg shadow-xl w-[920px] max-w-[96vw] max-h-[88vh] flex flex-col" onClick={(e)=> e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-sm">Uploads-Bibliothek</div>
            <div className="text-[10px] text-slate-500">Wähle eine Datei aus, um sie wiederzuverwenden</div>
          </div>
          <button className="text-xs px-2 py-1 rounded border bg-background hover:bg-slate-50" onClick={onClose}>{t('common.close')}</button>
        </div>

        <div className="px-4 py-3 border-b flex items-center gap-3">
          <select
            value={folderPrefix}
            onChange={(e)=>{ setFolderPrefix(e.target.value); setCursorByPage({}); setPage(1); }}
            className="text-xs px-2 py-1 border rounded bg-background"
            title="Ordner"
          >
            {folders.map((f)=> (
              <option key={f.prefix} value={f.prefix}>{folderLabel(f)}</option>
            ))}
          </select>
          <input
            value={query}
            onChange={(e)=>{ setQuery(e.target.value); setCursorByPage({}); setPage(1); }}
            placeholder="Suchen…"
            className="flex-1 text-xs px-2 py-1 border rounded outline-none focus:border-blue-400"
          />
          <div className="flex gap-1">
            {['all','page','newsletter'].map(s => (
              <button
                key={s}
                onClick={()=>{ setScope(s); setFolderPrefix(''); setFolders([]); setCursorByPage({}); setPage(1); }}
                className={`text-[11px] px-2 py-1 rounded border ${scope===s ? 'bg-slate-900 text-white border-slate-900' : 'bg-background hover:bg-slate-50'}`}
              >{scopeLabel(s)}</button>
            ))}
          </div>
        </div>

        <div className="p-4 flex-1 min-h-0 overflow-y-auto">
          {loading && <div className="text-xs text-slate-500">Lade Uploads…</div>}
          {!loading && items.length === 0 && <div className="text-xs text-slate-500">Keine Uploads gefunden.</div>}

          <div className="grid grid-cols-3 gap-3">
            {items.map((it) => {
              const vid = isVideo(it.key || it.url);
              return (
                <button
                  key={it.key}
                  className="group text-left border rounded overflow-hidden bg-background hover:shadow-sm hover:border-slate-300 relative"
                  onClick={()=> onPick?.(it.url)}
                  title={it.key}
                >
                  <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                    {vid ? (
                      <video src={it.url} className="w-full h-full object-cover" muted playsInline />
                    ) : (
                      <img src={it.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    )}
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] text-slate-700 truncate">{String(it.key||'').split('/').pop()}</div>
                    <div className="text-[9px] text-slate-400 truncate">{it.scope || ''}</div>
                  </div>
                  <button
                    type="button"
                    className="absolute top-2 right-2 text-[11px] px-2 py-1 rounded border bg-background/90 hover:bg-red-50 hover:border-red-300 hover:text-red-700 opacity-0 group-hover:opacity-100 transition"
                    title="Löschen"
                    onClick={async(e)=>{
                      e.preventDefault();
                      e.stopPropagation();
                      if(!token) return;
                      const ok = window.confirm('Diese Datei wirklich löschen?');
                      if(!ok) return;
                      try{
                        await api.deleteAssetItem(token, it.key);
                        setRefreshKey(k => k + 1);
                      }catch(err){
                        toast({ title: err.message || t('common.error'), variant: 'destructive' });
                      }
                    }}
                  >🗑</button>
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-4 py-3 border-t flex items-center justify-between">
          <div className="text-[11px] text-slate-500">
            {typeof total === 'number'
              ? `Seite ${page} · ${total} Dateien`
              : `Seite ${page}`}
          </div>
          <div className="flex gap-2">
            <button
              className={`text-xs px-3 py-1 rounded border ${canPrev ? 'bg-background hover:bg-slate-50' : 'bg-slate-50 text-slate-400 cursor-not-allowed'}`}
              disabled={!canPrev}
              onClick={()=> setPage(p => Math.max(1, p-1))}
            >Zurück</button>
            <button
              className={`text-xs px-3 py-1 rounded border ${canNext ? 'bg-background hover:bg-slate-50' : 'bg-slate-50 text-slate-400 cursor-not-allowed'}`}
              disabled={!canNext}
              onClick={()=> setPage(p => p+1)}
            >Weiter</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccordionSection({ title, children, defaultOpen }){
  const [open, setOpen] = React.useState(defaultOpen !== false);
  return (
    <div className='border rounded'>
      <button type='button' className='w-full flex items-center justify-between px-2 py-1 text-[11px] bg-slate-50 hover:bg-slate-100 border-b' onClick={()=> setOpen(o=>!o)}>
        <span className='font-medium text-slate-700'>{title}</span>
        <span className='text-slate-500'>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className='p-2'>
          {children}
        </div>
      )}
    </div>
  );
}

function groupFields(fields){
  // If fields already contain grouping metadata, use it. Otherwise return a single default group.
  if(!Array.isArray(fields) || fields.length === 0) return [];
  const hasGroups = fields.some(f => !!f.group);
  if(!hasGroups){
    return [{ title:'Allgemein', items: fields, defaultOpen: true }];
  }
  const map = new Map();
  for(const f of fields){
    const g = f.group || 'Allgemein';
    if(!map.has(g)) map.set(g, []);
    map.get(g).push(f);
  }
  const out = Array.from(map.entries()).map(([title, items])=> ({ title, items, defaultOpen: title === 'Allgemein' || title === 'Basics' }));
  return out;
}

function getFields(type){
  switch(type){
    case 'emailCode': return [
      { name: 'code', label: 'Demo-Code (Vorschau)', type: 'text' },
      { name: 'textAlign', label: 'Text Ausrichtung', type: 'select', options:[
        {value:'',label:'default'},{value:'left',label:'links'},{value:'center',label:'zentriert'},{value:'right',label:'rechts'}
      ] },
    ];
    case 'emailQr': return [
      { name: 'qrScale', label: 'QR Scale', type: 'number' },
      { name: 'qrMargin', label: 'QR Margin', type: 'number' },
      { name: 'textAlign', label: 'Text Ausrichtung', type: 'select', options:[
        {value:'',label:'default'},{value:'left',label:'links'},{value:'center',label:'zentriert'},{value:'right',label:'rechts'}
      ] },
      { name: 'code', label: 'Code (Vorschau)', type: 'text' },
    ];
    case 'claimButton': return [
      { name: 'label', label: 'Label', type: 'text' },
      { name: 'textAlign', label: 'Text Ausrichtung', type: 'select', options:[
        {value:'',label:'default'},{value:'left',label:'links'},{value:'center',label:'zentriert'},{value:'right',label:'rechts'}
      ] },
      { name: 'actionId', label: 'Aktion ID (für individuellen Code)', type: 'text' },
      // Dedicated button color fields (fallback to wrapper background if empty)
      { name: 'buttonBackground', label: 'Button Farbe', type: 'color' },
      { name: 'buttonColor', label: 'Textfarbe Button', type: 'color' },
      { name: 'paddingX', label: 'Padding X (px)', type: 'number' },
      { name: 'paddingY', label: 'Padding Y (px)', type: 'number' },
      { name: 'borderRadiusPx', label: 'Radius (px)', type: 'number' },
    ];
    case 'divider': return [
      { name: 'src', label: 'Video-URL', type: 'text' },
      { name: 'poster', label: 'Poster/Thumbnail-URL', type: 'image' },
      { name: 'style', label: 'Linienstil', type: 'select', options:[
        {value:'solid',label:'solid'}, {value:'dashed',label:'dashed'}, {value:'dotted',label:'dotted'}
      ] },
      { name: 'color', label: 'Farbe', type: 'color' },
      { name: 'maxWidthPx', label: 'Max Breite (px)', type: 'number' },
      { name: 'marginY', label: 'Abstand oben/unten (px)', type: 'number' },
  { name: 'label', label: 'Titel (optional)', type: 'richtext' },
      { name: 'align', label: 'Titel-Ausrichtung', type: 'select', options:[
        {value:'center',label:'zentriert'},{value:'left',label:'links'},{value:'right',label:'rechts'}
      ] },
      { name: 'labelColor', label: 'Titel Farbe', type: 'color' },
      { name: 'labelBackground', label: 'Titel Hintergrund', type: 'color' },
      { name: 'labelPaddingX', label: 'Titel Padding X (px)', type: 'number' },
      { name: 'labelPaddingY', label: 'Titel Padding Y (px)', type: 'number' },
      { name: 'labelFontSize', label: 'Titel Schriftgröße (px)', type: 'number' },
      { name: 'icon', label: 'Icon Mitte (optional, Emoji/Text)', type: 'text' },
      { name: 'leftIcon', label: 'Icon links (optional)', type: 'text' },
      { name: 'rightIcon', label: 'Icon rechts (optional)', type: 'text' },
      { name: 'capColor', label: 'Icon Farbe', type: 'color' },
      { name: 'capFontSize', label: 'Icon Schriftgröße (px)', type: 'number' },
    ];
    case 'spacer': return [
      { name: 'heightPx', label: 'Höhe Basis (px)', type: 'number' },
      { name: 'mobileHeightPx', label: 'Höhe Mobil (px)', type: 'number' },
      { name: 'tabletHeightPx', label: 'Höhe ≥640px (px)', type: 'number' },
      { name: 'mdHeightPx', label: 'Höhe ≥768px (px)', type: 'number' },
      { name: 'desktopHeightPx', label: 'Höhe ≥1024px (px)', type: 'number' }
    ];
    case 'heading': return [
      { name: 'text', label: 'Text', type: 'richtext' },
      { name: 'level', label: 'Level (1-6)', type: 'number' }
    ];
    case 'text': return [
      { name: 'text', label: 'Text', type: 'richtext' }
    ];
    case 'banner': return [
      { name: 'src', label: 'Quelle (Upload/URL)', type: 'media' },
      { name: 'type', label: 'Typ', type: 'select', options:[{value:'auto',label:'auto'},{value:'image',label:'Bild'},{value:'video',label:'Video'}] },
      { name: 'alt', label: 'Alt-Text', type: 'text' },
      { name: 'objectFit', label: 'Fit', type: 'select', options:[{value:'cover',label:'cover'},{value:'contain',label:'contain'},{value:'fill',label:'fill'}] },
      { name: 'heightPx', label: 'Höhe (px)', type: 'number' },
      { name: 'maxWidthPx', label: 'Max Breite (px)', type: 'number' },
      { name: 'borderRadiusPx', label: 'Radius (px)', type: 'number' },
      { name: 'autoplay', label: 'Autoplay (Video)', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'muted', label: 'Muted (Video)', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'loop', label: 'Loop (Video)', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'controls', label: 'Controls (Video)', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
    ];
    case 'companyLogo': return [
      { name: 'sizePx', label: 'Größe (px)', type: 'number' },
      { name: 'borderRadiusPx', label: 'Radius (px)', type: 'number' },
      { name: 'rounded', label: 'Rund', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
    ];
    case 'button': return [
      { name: 'label', label: 'Label', type: 'richtext', group: 'Allgemein' },
      { name: 'href', label: 'Link (optional)', type: 'text', group: 'Allgemein' },
      { name: 'variant', label: 'Variant', type: 'select', options: [ {value:'primary', label:'Primary'}, {value:'secondary', label:'Secondary'} ], group: 'Allgemein' },
      // Shape & layout
      { name: 'fullWidth', label: 'Breite 100%', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Layout' },
      { name: 'borderRadius', label: 'Ecken', type: 'select', options:[
        {value:'',label:'default'}, {value:'none',label:'none'}, {value:'sm',label:'sm'}, {value:'md',label:'md'}, {value:'lg',label:'lg'}, {value:'xl',label:'xl'}, {value:'full',label:'full'}
      ], group: 'Layout' },
      { name: 'borderRadiusPx', label: 'Radius (px, optional)', type: 'number', group: 'Layout' },
      { name: 'paddingX', label: 'Padding X (px)', type: 'number', group: 'Layout' },
      { name: 'paddingY', label: 'Padding Y (px)', type: 'number', group: 'Layout' },
      { name: 'leftIcon', label: 'Icon links (Text/Emoji)', type: 'text', group: 'Icons' },
      { name: 'rightIcon', label: 'Icon rechts (Text/Emoji)', type: 'text', group: 'Icons' },
      { name: 'iconGapPx', label: 'Icon Abstand (px)', type: 'number', group: 'Icons' },
      // Borders & outline
      { name: 'outline', label: 'Outline', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Umrandung' },
      { name: 'borderWidth', label: 'Border Breite (px)', type: 'number', group: 'Umrandung' },
      { name: 'borderColor', label: 'Border Farbe', type: 'color', group: 'Umrandung' },
      // Hover Styles
      { name: 'hoverBackground', label: 'Hover Hintergrund', type: 'color', group: 'Hover' },
      { name: 'hoverColor', label: 'Hover Textfarbe', type: 'color', group: 'Hover' },
      { name: 'hoverBorderColor', label: 'Hover Border Farbe', type: 'color', group: 'Hover' },
      // Focus Ring
      { name: 'focusRingColor', label: 'Fokus Ring Farbe', type: 'color', group: 'Fokus' },
      { name: 'focusRingWidthPx', label: 'Fokus Ring Breite (px)', type: 'number', group: 'Fokus' },
      // Disabled State
      { name: 'disabled', label: 'Disabled', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Disabled' },
      { name: 'disabledBackground', label: 'Disabled Hintergrund', type: 'color', group: 'Disabled' },
      { name: 'disabledColor', label: 'Disabled Textfarbe', type: 'color', group: 'Disabled' },
      { name: 'disabledBorderColor', label: 'Disabled Border Farbe', type: 'color', group: 'Disabled' },
      // Shadow
      { name: 'boxShadow', label: 'Schatten', type: 'select', options:[{value:'',label:'none'},{value:'sm',label:'sm'},{value:'md',label:'md'},{value:'lg',label:'lg'},{value:'xl',label:'xl'}], group: 'Schatten' },
      { name: 'hoverBoxShadow', label: 'Hover Schatten', type: 'select', options:[{value:'',label:'none'},{value:'sm',label:'sm'},{value:'md',label:'md'},{value:'lg',label:'lg'},{value:'xl',label:'xl'}], group: 'Schatten' },
    ];
    case 'section': return [
      { name: 'padding', label: 'Padding', type: 'select', options: [ {value:'', label:'(none)'}, {value:'sm', label:'sm'}, {value:'md', label:'md'}, {value:'lg', label:'lg'}, {value:'xl', label:'xl'} ] },
      { name: 'background', label: 'Background (Token/Hex)', type: 'text' }
    ];
    case 'container': return [
      { name: 'maxWidth', label: 'Max Width', type: 'select', options: [ {value:'', label:'default'}, {value:'md', label:'md'}, {value:'lg', label:'lg'} ] }
    ];
    case 'row': return [
      { name: 'gapPx', label: 'Abstand (px)', type: 'number' },
      { name: 'align', label: 'Vertikale Ausrichtung', type: 'select', options:[
        { value:'start', label:'oben' }, { value:'center', label:'zentriert' }, { value:'end', label:'unten' }, { value:'stretch', label:'stretch' }
      ] },
      { name: 'justify', label: 'Horizontale Ausrichtung', type: 'select', options:[
        { value:'start', label:'links' }, { value:'center', label:'zentriert' }, { value:'end', label:'rechts' }, { value:'between', label:'zwischen' }, { value:'around', label:'um' }, { value:'evenly', label:'gleichmäßig' }
      ] },
      { name: 'wrap', label: 'Umbruch', type: 'select', options:[ {value:'',label:'nein'}, {value:'1',label:'ja'} ] },
    ];
    case 'codeRequestForm': return [
      { name: 'showName', label: 'Name Feld anzeigen', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'nameRequired', label: 'Name Pflicht', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'showConsent', label: 'Checkbox anzeigen', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'consentRequired', label: 'Checkbox Pflicht', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'nameLabel', label: 'Name Label', type: 'text' },
      { name: 'emailLabel', label: 'E-Mail Label', type: 'text' },
      { name: 'placeholderName', label: 'Name Placeholder', type: 'text' },
      { name: 'placeholderEmail', label: 'E-Mail Placeholder', type: 'text' },
      { name: 'placeholder', label: 'Fallback Placeholder', type: 'text' },
      { name: 'submitLabel', label: 'Submit Label', type: 'richtext' },
      { name: 'submitLabelLoading', label: 'Loading Label', type: 'richtext' },
      { name: 'consentLabel', label: 'Checkbox Text', type: 'richtext' },
      { name: 'disclaimer', label: 'Disclaimer', type: 'richtext' },
      { name: 'textAlign', label: 'Text Align', type: 'select', options: [{value:'',label:'default'},{value:'left',label:'left'},{value:'center',label:'center'},{value:'right',label:'right'}] },
      { name: 'color', label: 'Text Farbe', type: 'color' }
    ];
    case 'codeRequestGroup': return [
      { name: 'layout', label: 'Layout', type: 'select', options:[{value:'vertical',label:'vertical'}] },
      { name: 'gap', label: 'Abstand', type: 'text' }
    ];
    case 'codeRequestName': return [
      { name: 'label', label: 'Label', type: 'richtext' },
      { name: 'placeholder', label: 'Placeholder', type: 'text' },
      { name: 'required', label: 'Pflicht', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'color', label: 'Text Farbe', type: 'color' }
    ];
    case 'codeRequestEmail': return [
      { name: 'label', label: 'Label', type: 'richtext' },
      { name: 'placeholder', label: 'Placeholder', type: 'text' },
      { name: 'required', label: 'Pflicht', type: 'select', options:[{value:'0',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'color', label: 'Text Farbe', type: 'color' }
    ];
    case 'codeRequestConsent': return [
      { name: 'label', label: 'Text (HTML)', type: 'richtext' },
      { name: 'required', label: 'Pflicht', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}] },
      { name: 'color', label: 'Text Farbe', type: 'color' }
    ];
    case 'codeRequestDropdown': return [
      { name: 'label', label: 'Frage / Label', type: 'richtext', group: 'Allgemein' },
      { name: 'placeholder', label: 'Placeholder', type: 'text', group: 'Allgemein' },
      { name: 'fieldName', label: 'Feldname (intern, eindeutig)', type: 'text', group: 'Allgemein' },
      { name: 'required', label: 'Pflichtfeld', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Allgemein' },
      { name: 'options', label: 'Auswahlmöglichkeiten', type: 'optionsList', group: 'Optionen' },
      { name: 'gateSubmit', label: 'Code nur bei bestimmter Auswahl', type: 'select', options:[{value:'',label:'nein – immer absenden'},{value:'1',label:'ja – nur mit Pflichtauswahl'}], group: 'Bedingter Zugang' },
      { name: 'requiredValue', label: 'Pflicht-Wert (Wert-Feld der Option)', type: 'text', group: 'Bedingter Zugang' },
      { name: 'gateErrorMsg', label: 'Fehlermeldung bei falscher Auswahl', type: 'text', group: 'Bedingter Zugang' },
      { name: 'color', label: 'Text Farbe', type: 'color', group: 'Stil' }
    ];
    case 'codeRequestSubmit': return [
      { name: 'submitLabel', label: 'Label', type: 'richtext', group: 'Allgemein' },
      { name: 'submitLabelLoading', label: 'Label (Laden)', type: 'richtext', group: 'Allgemein' },
      // Button-like styling options
      { name: 'variant', label: 'Variant', type: 'select', options: [ {value:'primary', label:'Primary'}, {value:'secondary', label:'Secondary'} ], group: 'Allgemein' },
      { name: 'fullWidth', label: 'Breite 100%', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Layout' },
      { name: 'borderRadius', label: 'Ecken', type: 'select', options:[
        {value:'',label:'default'}, {value:'none',label:'none'}, {value:'sm',label:'sm'}, {value:'md',label:'md'}, {value:'lg',label:'lg'}, {value:'xl',label:'xl'}, {value:'full',label:'full'}
      ], group: 'Layout' },
      { name: 'borderRadiusPx', label: 'Radius (px, optional)', type: 'number', group: 'Layout' },
      { name: 'paddingX', label: 'Padding X (px)', type: 'number', group: 'Layout' },
      { name: 'paddingY', label: 'Padding Y (px)', type: 'number', group: 'Layout' },
      { name: 'leftIcon', label: 'Icon links (Text/Emoji)', type: 'text', group: 'Icons' },
      { name: 'rightIcon', label: 'Icon rechts (Text/Emoji)', type: 'text', group: 'Icons' },
      { name: 'iconGapPx', label: 'Icon Abstand (px)', type: 'number', group: 'Icons' },
      { name: 'outline', label: 'Outline', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Umrandung' },
      { name: 'borderWidth', label: 'Border Breite (px)', type: 'number', group: 'Umrandung' },
      { name: 'borderColor', label: 'Border Farbe', type: 'color', group: 'Umrandung' },
      { name: 'hoverBackground', label: 'Hover Hintergrund', type: 'color', group: 'Hover' },
      { name: 'hoverColor', label: 'Hover Textfarbe', type: 'color', group: 'Hover' },
      { name: 'hoverBorderColor', label: 'Hover Border Farbe', type: 'color', group: 'Hover' },
      { name: 'focusRingColor', label: 'Fokus Ring Farbe', type: 'color', group: 'Fokus' },
      { name: 'focusRingWidthPx', label: 'Fokus Ring Breite (px)', type: 'number', group: 'Fokus' },
      { name: 'disabled', label: 'Disabled', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Disabled' },
      { name: 'disabledBackground', label: 'Disabled Hintergrund', type: 'color', group: 'Disabled' },
      { name: 'disabledColor', label: 'Disabled Textfarbe', type: 'color', group: 'Disabled' },
      { name: 'disabledBorderColor', label: 'Disabled Border Farbe', type: 'color', group: 'Disabled' },
      { name: 'boxShadow', label: 'Schatten', type: 'select', options:[{value:'',label:'none'},{value:'sm',label:'sm'},{value:'md',label:'md'},{value:'lg',label:'lg'},{value:'xl',label:'xl'}], group: 'Schatten' },
      { name: 'hoverBoxShadow', label: 'Hover Schatten', type: 'select', options:[{value:'',label:'none'},{value:'sm',label:'sm'},{value:'md',label:'md'},{value:'lg',label:'lg'},{value:'xl',label:'xl'}], group: 'Schatten' },
      // Colors from base style
      { name: 'color', label: 'Text Farbe', type: 'color', group: 'Allgemein' },
      { name: 'background', label: 'Hintergrund', type: 'color', group: 'Allgemein' }
    ];
    case 'codeDisplay': return [
      { name: 'heading', label: 'Überschrift', type: 'richtext' },
      { name: 'code', label: 'Code (Preview Dummy)', type: 'text' },
      { name: 'hint', label: 'Hinweis', type: 'richtext' },
      { name: 'textAlign', label: 'Text Align', type: 'select', options: [{value:'',label:'default'},{value:'center',label:'center'}] },
      { name: 'showQr', label: 'QR anzeigen', type: 'select', options:[{value:'',label:'ja'},{value:'0',label:'nein'}] },
      { name: 'qrScale', label: 'QR Scale (px unit)', type: 'number' },
      { name: 'qrMargin', label: 'QR Rand (px)', type: 'number' },
      { name: 'showCopyLink', label: 'Copy-Link anzeigen', type: 'select', options:[{value:'',label:'ja'},{value:'0',label:'nein'}] },
      { name: 'copyLabel', label: 'Copy-Link Label', type: 'richtext' },
      { name: 'color', label: 'Text Farbe', type: 'color' },
      { name: 'fontSize', label: 'Font Size (z.B. 24px)', type: 'text' }
    ];
    case 'countdown': return [
      // Design Presets
      { name: 'preset', label: 'Design', type: 'select', options:[
        {value:'', label:'Benutzerdefiniert'},
        {value:'modern', label:'Modern (Lila Gradient)'},
        {value:'elegant', label:'Elegant (Dunkelblau)'},
        {value:'minimal', label:'Schlicht (Weiß/Grau)'},
        {value:'vibrant', label:'Lebendig (Orange/Pink)'},
        {value:'dark', label:'Dunkel (Schwarz/Gold)'},
      ], group: 'Allgemein' },
      // Eigenes Datum + Uhrzeit für Newsletter
      { name: 'customTarget', label: 'Zieldatum & Uhrzeit', type: 'datetime-local', group: 'Allgemein' },
      { name: 'tickMs', label: 'Aktualisierung (ms)', type: 'number', group: 'Allgemein' },
      // Units
      { name: 'showDays', label: 'Tage anzeigen', type: 'select', options:[{value:'',label:'ja'},{value:'0',label:'nein'}], group: 'Einheiten' },
      { name: 'showHours', label: 'Stunden anzeigen', type: 'select', options:[{value:'',label:'ja'},{value:'0',label:'nein'}], group: 'Einheiten' },
      { name: 'showMinutes', label: 'Minuten anzeigen', type: 'select', options:[{value:'',label:'ja'},{value:'0',label:'nein'}], group: 'Einheiten' },
      { name: 'showSeconds', label: 'Sekunden anzeigen', type: 'select', options:[{value:'',label:'nein (default)'},{value:'1',label:'ja'}], group: 'Einheiten' },
      { name: 'pad', label: 'Zweistellig (h/m/s)', type: 'select', options:[{value:'',label:'ja (default)'},{value:'0',label:'nein'}], group: 'Einheiten' },
      // Title
      { name: 'title', label: 'Titel (optional)', type: 'richtext', group: 'Titel' },
      { name: 'titlePosition', label: 'Titel Position', type: 'select', options:[
        {value:'above',label:'oberhalb'}, {value:'below',label:'unterhalb'}, {value:'left',label:'links'}, {value:'right',label:'rechts'}
      ], group: 'Titel' },
      { name: 'titleAlign', label: 'Titel Ausrichtung (oben/unten)', type: 'select', options:[
        {value:'left',label:'links'},{value:'center',label:'zentriert'},{value:'right',label:'rechts'}
      ], group: 'Titel' },
      { name: 'titleFontSize', label: 'Titel Schriftgröße (px)', type: 'number', group: 'Titel' },
      { name: 'titleGapPx', label: 'Abstand Titel ↔ Countdown (px)', type: 'number', group: 'Titel' },
      // Labels
      { name: 'labelDays', label: 'Label Tage', type: 'text', group: 'Labels' },
      { name: 'labelHours', label: 'Label Stunden', type: 'text', group: 'Labels' },
      { name: 'labelMinutes', label: 'Label Minuten', type: 'text', group: 'Labels' },
      { name: 'labelSeconds', label: 'Label Sekunden', type: 'text', group: 'Labels' },
      { name: 'showLabels', label: 'Labels anzeigen', type: 'select', options:[{value:'',label:'ja'},{value:'0',label:'nein'}], group: 'Labels' },
      // Layout & Style
      { name: 'layout', label: 'Layout', type: 'select', options:[{value:'blocks',label:'Blöcke'},{value:'inline',label:'Inline'}], group: 'Layout & Stil' },
      { name: 'separator', label: 'Trenner (Inline)', type: 'text', group: 'Layout & Stil' },
      { name: 'digitFontSize', label: 'Ziffern Schriftgröße (px)', type: 'number', group: 'Layout & Stil' },
      { name: 'labelFontSize', label: 'Label Schriftgröße (px)', type: 'number', group: 'Layout & Stil' },
      { name: 'gapPx', label: 'Abstand zwischen Blöcken (px)', type: 'number', group: 'Layout & Stil' },
      { name: 'inlineGapPx', label: 'Abstand Inline (px)', type: 'number', group: 'Layout & Stil' },
      { name: 'blockBackground', label: 'Block Hintergrund', type: 'color', group: 'Layout & Stil' },
      { name: 'blockBorderColor', label: 'Block Border Farbe', type: 'color', group: 'Layout & Stil' },
      { name: 'blockRadiusPx', label: 'Block Radius (px)', type: 'number', group: 'Layout & Stil' },
      { name: 'blockPadX', label: 'Block Padding X (px)', type: 'number', group: 'Layout & Stil' },
      { name: 'blockPadY', label: 'Block Padding Y (px)', type: 'number', group: 'Layout & Stil' },
      { name: 'blockMinWidthPx', label: 'Block min Breite (px)', type: 'number', group: 'Layout & Stil' },
      // Texts
      { name: 'subtitle', label: 'Unterschrift (unter Countdown)', type: 'text', group: 'Texte' },
      { name: 'prefix', label: 'Text vor dem Countdown', type: 'richtext', group: 'Texte' },
      { name: 'suffix', label: 'Text nach dem Countdown', type: 'richtext', group: 'Texte' },
      { name: 'endedText', label: 'Text nach Ablauf', type: 'richtext', group: 'Texte' },
      { name: 'hideWhenEnded', label: 'Nach Ablauf ausblenden', type: 'select', options:[{value:'',label:'nein'},{value:'1',label:'ja'}], group: 'Texte' },
      // Colors (base)
      { name: 'color', label: 'Text Farbe (Basis)', type: 'color', group: 'Farben' },
      { name: 'background', label: 'Hintergrund (Basis)', type: 'color', group: 'Farben' },
      { name: 'gradientFrom', label: 'Gradient Start', type: 'color', group: 'Farben' },
      { name: 'gradientTo', label: 'Gradient Ende', type: 'color', group: 'Farben' },
      { name: 'digitColor', label: 'Ziffern Farbe', type: 'color', group: 'Farben' },
      { name: 'labelColor', label: 'Label Farbe (Tage/Stunden...)', type: 'color', group: 'Farben' },
      { name: 'subtextColor', label: 'Unterschrift Farbe', type: 'color', group: 'Farben' },
    ];
    case 'image': return [
      { name: 'src', label: 'Bild-URL', type: 'image' },
      { name: 'alt', label: 'Alt-Text', type: 'text' },
      { name: 'href', label: 'Link-URL (optional)', type: 'text' },
      { name: 'maxWidth', label: 'Max. Breite (z.B. 600px, 100%)', type: 'text' },
      { name: 'align', label: 'Ausrichtung', type: 'select', options:[
        {value:'left',label:'links'},{value:'center',label:'zentriert'},{value:'right',label:'rechts'}
      ] },
      { name: 'borderRadius', label: 'Ecken-Radius (z.B. 8px)', type: 'text' },
    ];
    case 'video': return [
      { name: 'src', label: 'Video-URL', type: 'video' },
      { name: 'poster', label: 'Vorschaubild-URL (Poster)', type: 'image' },
      { name: 'alt', label: 'Alt-Text', type: 'text' },
      { name: 'maxWidth', label: 'Max. Breite (z.B. 600px, 100%)', type: 'text' },
      { name: 'controls', label: 'Steuerelemente', type: 'select', options:[{value:'',label:'ja (default)'},{value:'0',label:'nein'}] },
      { name: 'autoplay', label: 'Autoplay', type: 'select', options:[{value:'',label:'nein (default)'},{value:'1',label:'ja'}] },
      { name: 'loop', label: 'Wiederholen', type: 'select', options:[{value:'',label:'nein (default)'},{value:'1',label:'ja'}] },
      { name: 'muted', label: 'Stumm', type: 'select', options:[{value:'',label:'nein (default)'},{value:'1',label:'ja'}] },
      { name: 'align', label: 'Ausrichtung', type: 'select', options:[
        {value:'left',label:'links'},{value:'center',label:'zentriert'},{value:'right',label:'rechts'}
      ] },
      { name: 'borderRadius', label: 'Ecken-Radius (z.B. 8px)', type: 'text' },
    ];
    case 'newsletterFooter': return [
      { name: 'companyName', label: 'Firmenname', type: 'text', group: 'Inhalt' },
      { name: 'companyAddress', label: 'Adresse', type: 'textarea', group: 'Inhalt' },
      { name: 'backgroundColor', label: 'Hintergrundfarbe', type: 'color', group: 'Design' },
      { name: 'textColor', label: 'Textfarbe', type: 'color', group: 'Design' },
      { name: 'linkColor', label: 'Linkfarbe', type: 'color', group: 'Design' },
      { name: 'fontSize', label: 'Schriftgröße (z.B. 12px)', type: 'text', group: 'Design' },
      { name: 'padding', label: 'Padding (z.B. 30px 20px)', type: 'text', group: 'Design' },
    ];
    case 'socials': return [
      { name: 'facebookUrl', label: 'Facebook URL', type: 'text', group: 'Links' },
      { name: 'twitterUrl', label: 'Twitter/X URL', type: 'text', group: 'Links' },
      { name: 'instagramUrl', label: 'Instagram URL', type: 'text', group: 'Links' },
      { name: 'linkedinUrl', label: 'LinkedIn URL', type: 'text', group: 'Links' },
      { name: 'youtubeUrl', label: 'YouTube URL', type: 'text', group: 'Links' },
      { name: 'iconSize', label: 'Icon-Größe (px)', type: 'number', group: 'Design' },
      { name: 'gap', label: 'Abstand zwischen Icons (px)', type: 'number', group: 'Design' },
      { name: 'align', label: 'Ausrichtung', type: 'select', options:[
        {value:'left',label:'links'},{value:'center',label:'zentriert'},{value:'right',label:'rechts'}
      ], group: 'Design' },
    ];
    case 'columns1':
    case 'columns2':
    case 'columns2_2575':
    case 'columns2_7525':
    case 'columns3':
    case 'columns3_252550':
    case 'columns3_255025':
    case 'columns3_502525':
    case 'columns4':
      return [
        { name: 'gap', label: 'Spaltenabstand (px)', type: 'number' },
        { name: 'padding', label: 'Innen-Abstand', type: 'select', options:[
          {value:'',label:'none'},{value:'sm',label:'sm'},{value:'md',label:'md'},{value:'lg',label:'lg'}
        ]},
      ];
    default: return [];
  }
}

function OptionsListEditor({ value, onChange }) {
  // value is a JSON string: [{ label, value }]
  const [options, setOptions] = React.useState(() => {
    try { const p = JSON.parse(value); return Array.isArray(p) ? p.map(o => typeof o === 'string' ? { label: o, value: o } : o) : []; }
    catch { return []; }
  });

  // Sync internal state when external value changes
  React.useEffect(() => {
    try {
      const p = JSON.parse(value);
      if (Array.isArray(p)) setOptions(p.map(o => typeof o === 'string' ? { label: o, value: o } : o));
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function commit(next) {
    setOptions(next);
    onChange(JSON.stringify(next));
  }

  function addOption() {
    commit([...options, { label: 'Option', value: `option_${options.length + 1}` }]);
  }

  function updateOption(idx, field, val) {
    const next = options.map((o, i) => i === idx ? { ...o, [field]: val } : o);
    commit(next);
  }

  function removeOption(idx) {
    commit(options.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-1.5">
      {options.map((o, idx) => (
        <div key={idx} className="flex items-center gap-1">
          <input
            className="border border-slate-200 bg-background rounded px-1.5 py-1 text-[11px] flex-1 min-w-0"
            placeholder="Label"
            value={o.label}
            onChange={e => updateOption(idx, 'label', e.target.value)}
          />
          <input
            className="border border-slate-200 bg-background rounded px-1.5 py-1 text-[11px] w-24 min-w-0"
            placeholder="Wert"
            value={o.value}
            onChange={e => updateOption(idx, 'value', e.target.value)}
          />
          <button
            type="button"
            onClick={() => removeOption(idx)}
            className="text-[10px] px-1.5 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 shrink-0"
            title="Entfernen"
          >✕</button>
        </div>
      ))}
      <button
        type="button"
        onClick={addOption}
        className="text-[10px] px-2 py-1 border rounded bg-background hover:bg-slate-50 w-full"
      >+ Option hinzufügen</button>
      {options.length === 0 && (
        <p className="text-[10px] text-slate-400 text-center">Noch keine Optionen</p>
      )}
    </div>
  );
}

function ColorInput({ value, onChange }){
  const { t } = useTranslation();
  // Accept hex or token strings. If non-hex, the picker shows a default color.
  const isHex = typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
  const colorValue = isHex ? value : '#000000';
  return (
    <div className='flex items-center gap-2'>
      <input
        type='color'
        className='h-6 w-6 p-0 border rounded cursor-pointer'
        value={colorValue}
        onChange={(e)=> onChange?.(e.target.value)}
        title={t('common.form.chooseColor')}
      />
      <input
        className='border rounded flex-1 px-2 py-1 text-[11px]'
        value={value || ''}
        onChange={(e)=> onChange?.(e.target.value)}
        placeholder='#RRGGBB oder Token'
      />
      <button
        className='text-[10px] px-2 py-1 border rounded bg-background hover:bg-slate-50'
        title={t('common.form.reset')}
        onClick={(e)=>{ e.preventDefault(); onChange?.(''); }}
      >
        Reset
      </button>
    </div>
  );
}

function AdvancedPropsEditor({ propsObj, onApply }){
  const [text, setText] = React.useState(() => {
    try { return JSON.stringify(propsObj, null, 2); } catch { return '{}'; }
  });
  const [error, setError] = React.useState('');
  function apply(){
    setError('');
    try {
      const obj = JSON.parse(text || '{}');
      if(typeof obj !== 'object' || Array.isArray(obj)) throw new Error('JSON muss ein Objekt sein');
      onApply?.(obj);
    } catch (e) {
      setError(String(e.message || e));
    }
  }
  return (
    <div>
      <div className='font-semibold mb-1'>Erweitert (JSON)</div>
      <textarea className='border rounded w-full px-2 py-1 text-[11px] h-40 font-mono' value={text} onChange={(e)=> setText(e.target.value)} spellCheck={false} />
      {error && <div className='text-[10px] text-red-600 mt-1'>{error}</div>}
      <div className='mt-2 flex gap-2'>
        <button className='text-[11px] px-3 py-1 rounded border bg-background hover:bg-slate-50' onClick={apply}>Übernehmen</button>
      </div>
    </div>
  );
}

// --- Simple Rich Text Editor for the 'text' component ---
const TOKENS = [
  { key:'name', label:'Name', newsletterOnly: true },
  { key:'email', label:'E-Mail', newsletterOnly: true },
  { key:'action.title', label:'Aktion Titel' },
  { key:'action.campaignName', label:'Kampagne' },
  { key:'action.companyName', label:'Unternehmen' },
  // Company address fields
  { key:'company.addressLine1', label:'Adresse Zeile 1' },
  { key:'company.addressLine2', label:'Adresse Zeile 2' },
  { key:'company.postalCode', label:'PLZ' },
  { key:'company.city', label:'Stadt' },
  { key:'company.country', label:'Land' },
];

function tokensToWrappers(html){
  if(!html) return '';
  let out = html;
  for(const t of TOKENS){
    const re = new RegExp(`\\{\\{\\s*${t.key.replace('.', '\\.')}\\s*\\}\\}`, 'g');
    out = out.replace(re, `<span data-token="${t.key}" class="rt-token" contenteditable="false" style="display:inline-block;background:#f1f5f9;border:1px solid #cbd5e1;color:#334155;font-size:11px;border-radius:8px;padding:1px 6px;">{{${t.key}}}</span>`);
  }
  return out;
}

function UploadButton({ label='Upload', accept, onSelect }){
  const inputRef = React.useRef(null);
  return (
    <>
      <input type='file' ref={inputRef} accept={accept} className='hidden' onChange={(e)=>{ const f = e.target.files && e.target.files[0]; if(f) onSelect?.(f); e.target.value=''; }} />
      <button className='text-[10px] px-2 py-1 border rounded bg-background hover:bg-slate-50' onClick={(e)=>{ e.preventDefault(); inputRef.current?.click(); }}>{label}</button>
    </>
  );
}
function wrappersToTokens(html){
  if(!html) return '';
  // Replace any span with data-token back to {{key}}
  return html.replace(/<span[^>]*data-token=["']([^"']+)["'][^>]*>.*?<\/span>/g, (_m, key)=> `{{${key}}}`);
}

function RichTextEditor({ value, onChange, isNewsletterEditor }){
  const { t } = useTranslation();
  const [isSource, setIsSource] = React.useState(false);
  const [showTokenPicker, setShowTokenPicker] = React.useState(false);
  const [tokenQuery, setTokenQuery] = React.useState('');
  const [showFontPicker, setShowFontPicker] = React.useState(false);
  const [fontQuery, setFontQuery] = React.useState('');
  const [fontItems, setFontItems] = React.useState(null); // null = not loaded, [] = loaded none
  const [fontsLoading, setFontsLoading] = React.useState(false);
  const editorRef = React.useRef(null);
  const toolbarRef = React.useRef(null);
  const savedRangeRef = React.useRef(null);
  const [inlineColor, setInlineColor] = React.useState('');
  const [inlineSize, setInlineSize] = React.useState('');
  
  // Filter tokens based on context
  const availableTokens = React.useMemo(() => {
    if (isNewsletterEditor) {
      return TOKENS.filter(t => t.newsletterOnly === true);
    }
    return TOKENS.filter(t => !t.newsletterOnly);
  }, [isNewsletterEditor]);

  React.useEffect(()=>{
    if(editorRef.current && !isSource){
      const desired = tokensToWrappers(value || '');
      if(editorRef.current.innerHTML !== desired){
        editorRef.current.innerHTML = desired;
      }
    }
  }, [value, isSource]);

  function exec(cmd, arg){
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
  }
  function onInput(){
    const html = editorRef.current?.innerHTML || '';
    const normalized = wrappersToTokens(html);
    onChange?.(normalized);
  }
  function onPaste(e){
    if(!isSource){
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text');
      document.execCommand('insertText', false, text);
    }
  }
  function saveSelection(){
    const sel = window.getSelection();
    if(!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if(editorRef.current && editorRef.current.contains(range.startContainer) && editorRef.current.contains(range.endContainer)){
      savedRangeRef.current = range.cloneRange();
    }
  }
  function placeCaretAtEnd(el){
    if(!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    savedRangeRef.current = range.cloneRange();
  }
  function restoreSelection(){
    const sel = window.getSelection();
    sel.removeAllRanges();
    if(savedRangeRef.current){
      sel.addRange(savedRangeRef.current);
    } else {
      placeCaretAtEnd(editorRef.current);
    }
  }
  function applyInlineStyle(styleObj){
    editorRef.current?.focus();
    restoreSelection();
    const sel = window.getSelection();
    if(!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // If selection is collapsed, insert an empty span with style and place caret inside
    if(range.collapsed){
      const span = document.createElement('span');
      Object.assign(span.style, styleObj);
      span.appendChild(document.createTextNode('\u200b'));
      range.insertNode(span);
      // Place caret inside span at end
      const newRange = document.createRange();
      newRange.setStart(span.firstChild, 1);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
      savedRangeRef.current = newRange.cloneRange();
      onInput();
      return;
    }
    // Wrap selected contents with styled span
    try {
      const frag = range.extractContents();
      const span = document.createElement('span');
      Object.assign(span.style, styleObj);
      span.appendChild(frag);
      range.insertNode(span);
      // Reselect after inserted node
      const newRange = document.createRange();
      newRange.setStartAfter(span);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
      savedRangeRef.current = newRange.cloneRange();
    } catch(e){
      // Fallback: use execCommand for color
      if(styleObj && styleObj.color){
        try{ document.execCommand('styleWithCSS', false, true); }catch{}
        document.execCommand('foreColor', false, styleObj.color);
      }
    }
    onInput();
  }
  function insertToken(key){
    const html = `<span data-token=\"${key}\" class=\"rt-token\" contenteditable=\"false\" style=\"display:inline-block;background:#f1f5f9;border:1px solid #cbd5e1;color:#334155;font-size:11px;border-radius:8px;padding:1px 6px;\">{{${key}}}</span>`;
    editorRef.current?.focus();
    // Restore caret to last known position inside editor
    restoreSelection();
    const sel = window.getSelection();
    let range = sel && sel.rangeCount ? sel.getRangeAt(0) : null;
    if(!range || !(editorRef.current && editorRef.current.contains(range.startContainer))){
      placeCaretAtEnd(editorRef.current);
      range = window.getSelection().getRangeAt(0);
    }
    // Insert HTML using Range API for reliable caret placement
    const container = document.createElement('div');
    container.innerHTML = html;
    const frag = document.createDocumentFragment();
    let node, lastNode;
    while((node = container.firstChild)){
      lastNode = frag.appendChild(node);
    }
    range.deleteContents();
    range.insertNode(frag);
    if(lastNode){
      // Move caret after inserted token
      range.setStartAfter(lastNode);
      range.setEndAfter(lastNode);
      sel.removeAllRanges();
      sel.addRange(range);
      savedRangeRef.current = range.cloneRange();
    }
    onInput();
    setShowTokenPicker(false);
    setTokenQuery('');
  }
  const filteredTokens = availableTokens.filter(t => (t.label + ' ' + t.key).toLowerCase().includes(tokenQuery.toLowerCase()))
  // --- Google Fonts ---
  const POPULAR_FONTS = React.useMemo(()=>[
    { family:'Inter', generic:'sans-serif' },
    { family:'Roboto', generic:'sans-serif' },
    { family:'Open Sans', generic:'sans-serif' },
    { family:'Lato', generic:'sans-serif' },
    { family:'Poppins', generic:'sans-serif' },
    { family:'Montserrat', generic:'sans-serif' },
    { family:'Nunito', generic:'sans-serif' },
    { family:'Rubik', generic:'sans-serif' },
    { family:'Work Sans', generic:'sans-serif' },
    { family:'Source Sans 3', generic:'sans-serif' },
    { family:'Noto Sans', generic:'sans-serif' },
    { family:'Noto Serif', generic:'serif' },
    { family:'Merriweather', generic:'serif' },
    { family:'Playfair Display', generic:'serif' },
    { family:'Libre Baskerville', generic:'serif' },
    { family:'PT Serif', generic:'serif' },
    { family:'Oswald', generic:'sans-serif' },
    { family:'Raleway', generic:'sans-serif' },
    { family:'Ubuntu', generic:'sans-serif' },
    { family:'Karla', generic:'sans-serif' },
    { family:'Heebo', generic:'sans-serif' },
    { family:'Mukta', generic:'sans-serif' },
    { family:'Overpass', generic:'sans-serif' },
    { family:'Asap', generic:'sans-serif' },
    { family:'Exo 2', generic:'sans-serif' },
    { family:'Barlow', generic:'sans-serif' },
    { family:'Catamaran', generic:'sans-serif' },
    { family:'Varela Round', generic:'sans-serif' },
    { family:'Zilla Slab', generic:'serif' },
    { family:'Arimo', generic:'sans-serif' },
    { family:'Archivo', generic:'sans-serif' },
    { family:'Cinzel', generic:'serif' },
    { family:'Dosis', generic:'sans-serif' },
    { family:'Josefin Sans', generic:'sans-serif' },
    { family:'Manrope', generic:'sans-serif' },
    { family:'Fira Sans', generic:'sans-serif' },
    { family:'Inconsolata', generic:'monospace' },
    { family:'JetBrains Mono', generic:'monospace' },
    { family:'Space Mono', generic:'monospace' },
    { family:'Source Code Pro', generic:'monospace' },
    { family:'IBM Plex Sans', generic:'sans-serif' },
    { family:'IBM Plex Mono', generic:'monospace' },
    { family:'DM Sans', generic:'sans-serif' },
    { family:'DM Serif Display', generic:'serif' },
    { family:'Outfit', generic:'sans-serif' },
    { family:'Quicksand', generic:'sans-serif' },
  ], []);
  function ensureFontLink(family){
    if(!family) return;
    const id = 'pb-font-inline-' + family.replace(/\s+/g,'+').toLowerCase();
    if(document.getElementById(id)) return;
    const href = `https://fonts.googleapis.com/css2?family=${family.replace(/\s+/g,'+')}:wght@400;600;700&display=swap`;
    const link = document.createElement('link');
    link.id = id; link.rel='stylesheet'; link.href = href;
    document.head.appendChild(link);
  }
  async function openFontPicker(){
    setShowFontPicker(v=>!v);
    if(fontItems === null){
      // First time open: try API fetch, else fallback
      setFontsLoading(true);
      try{
        const key = import.meta.env?.VITE_GOOGLE_FONTS_API_KEY;
        if(key){
          const url = `https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity&key=${key}`;
          const res = await fetch(url);
          if(res.ok){
            const json = await res.json();
            const items = (json.items || []).map(it=>({ family: it.family, generic: (it.category==='serif'?'serif': it.category==='monospace'?'monospace': it.category==='handwriting'?'cursive':'sans-serif') }));
            setFontItems(items);
          }else{
            setFontItems(POPULAR_FONTS);
          }
        }else{
          setFontItems(POPULAR_FONTS);
        }
      }catch{
        setFontItems(POPULAR_FONTS);
      }finally{
        setFontsLoading(false);
      }
    }
  }
  const fonts = Array.isArray(fontItems) ? fontItems : POPULAR_FONTS;
  const filteredFonts = fonts.filter(f=> f.family.toLowerCase().includes(fontQuery.toLowerCase()));

  return (
    <div className='border rounded relative'>
      <div ref={toolbarRef} className='flex flex-wrap gap-1 p-1 border-b bg-slate-50 text-[11px]'>
        <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>exec('bold')}>B</button>
        <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100 italic' onClick={()=>exec('italic')}>I</button>
        <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100 underline' onClick={()=>exec('underline')}>U</button>
        <div className='w-px bg-slate-200 mx-1' />
        <button type='button' title='Links ausrichten' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>exec('justifyLeft')}>⟸</button>
        <button type='button' title='Zentrieren' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>exec('justifyCenter')}>≡</button>
        <button type='button' title='Rechts ausrichten' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>exec('justifyRight')}>⟹</button>
        <button type='button' title='Blocksatz' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>exec('justifyFull')}>≣</button>
        <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>exec('insertUnorderedList')}>• Liste</button>
        <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>{ const url = prompt('Link URL eingeben'); if(url) exec('createLink', url); }}>Link</button>
        {/* Inline color */}
        <div className='flex items-center gap-1 px-1 border rounded bg-background'>
          <span className='text-[10px] text-slate-600 px-1'>Farbe</span>
          <input
            type='color'
            value={/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(inlineColor) ? inlineColor : '#000000'}
            onMouseDown={saveSelection}
            onChange={(e)=>{ const v=e.target.value; setInlineColor(v); applyInlineStyle({ color: v }); }}
          />
          <button type='button' className='px-1 py-[1px] text-[10px] hover:text-red-600' title='Farbe entfernen'
            onMouseDown={saveSelection}
            onClick={()=>{ setInlineColor(''); applyInlineStyle({ color: '' }); }}>
            ✕
          </button>
        </div>
        {/* Inline font family */}
        <div className='relative'>
          <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onMouseDown={saveSelection} onClick={openFontPicker}>Schriftart ▾</button>
          {showFontPicker && (
            <div className='absolute left-0 mt-1 w-72 bg-background border rounded shadow p-2 z-10'>
              <div className='flex items-center gap-1 mb-2'>
                <input autoFocus className='flex-1 border rounded px-2 py-1 text-[11px]' placeholder='Google Fonts suchen…' value={fontQuery} onChange={e=>setFontQuery(e.target.value)} />
                <button type='button' className='px-2 py-1 text-[10px] border rounded bg-background hover:bg-slate-50' title='Font entfernen'
                  onMouseDown={saveSelection}
                  onClick={()=>{ applyInlineStyle({ fontFamily: '' }); setShowFontPicker(false); }}>✕</button>
              </div>
              <div className='max-h-56 overflow-auto'>
                {fontsLoading && <div className='text-[10px] text-slate-500 px-1 py-2'>Lade Google Fonts…</div>}
                {!fontsLoading && filteredFonts.map(f => (
                  <button key={f.family} className='w-full text-left px-2 py-1 rounded hover:bg-slate-100'
                    onMouseDown={(e)=>e.preventDefault()}
                    onClick={()=>{ ensureFontLink(f.family); applyInlineStyle({ fontFamily: `'${f.family}', ${f.generic||'sans-serif'}` }); setShowFontPicker(false); }}>
                    <span className='text-[11px]' style={{ fontFamily: `'${f.family}', ${f.generic||'sans-serif'}` }}>{f.family}</span>
                  </button>
                ))}
                {!fontsLoading && !filteredFonts.length && <div className='text-[10px] text-slate-500 px-1 py-2'>Keine Treffer</div>}
              </div>
              <div className='mt-2 text-[10px] text-slate-500'>Tipp: Setze VITE_GOOGLE_FONTS_API_KEY um alle Schriften zu laden.</div>
            </div>
          )}
        </div>
        {/* Inline font size */}
        <div className='flex items-center gap-1 px-1 border rounded bg-background'>
          <span className='text-[10px] text-slate-600 px-1'>Größe</span>
          <input
            type='number'
            min={8}
            max={96}
            step={1}
            className='w-14 px-1 py-[2px] border rounded text-[11px]'
            value={inlineSize}
            onMouseDown={saveSelection}
            onChange={(e)=> setInlineSize(e.target.value)}
            onBlur={()=>{ const n = parseInt(inlineSize,10); if(!isNaN(n)){ applyInlineStyle({ fontSize: n + 'px' }); } }}
          />
          <button type='button' className='px-1 py-[1px] text-[10px] hover:text-red-600' title={t('common.form.removeSize')}
            onMouseDown={saveSelection}
            onClick={()=>{ setInlineSize(''); applyInlineStyle({ fontSize: '' }); }}>
            ✕
          </button>
        </div>
        <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=>exec('removeFormat')}>Format entfernen</button>
        <div className='flex-1' />
        <div className='relative'>
          <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onMouseDown={saveSelection} onClick={()=> setShowTokenPicker(v=>!v)}>Token ▾</button>
          {showTokenPicker && (
            <div className='absolute right-0 mt-1 w-64 bg-background border rounded shadow p-2 z-10'>
              <input autoFocus className='w-full border rounded px-2 py-1 text-[11px] mb-2' placeholder='Suche Token...' value={tokenQuery} onChange={e=>setTokenQuery(e.target.value)} />
              <div className='max-h-48 overflow-auto'>
                {filteredTokens.map(t => (
                  <button key={t.key} className='w-full text-left px-2 py-1 rounded hover:bg-slate-100 flex items-center gap-2' onMouseDown={(e)=>e.preventDefault()} onClick={()=> insertToken(t.key)}>
                    <span className='inline-block bg-slate-100 border border-slate-300 text-slate-700 text-[10px] rounded px-1.5 py-[1px]'>{`{{${t.key}}}`}</span>
                    <span className='text-[11px]'>{t.label}</span>
                  </button>
                ))}
                {!filteredTokens.length && <div className='text-[10px] text-slate-500 px-1 py-2'>Keine Treffer</div>}
              </div>
            </div>
          )}
        </div>
        <button type='button' className='px-2 py-[2px] border rounded bg-background hover:bg-slate-100' onClick={()=> setIsSource(s=>!s)}>{isSource ? 'Visuell' : 'HTML'}</button>
      </div>
      {isSource ? (
        <textarea className='w-full h-40 p-2 text-[11px] font-mono outline-none'
          value={value || ''}
          onChange={(e)=> onChange?.(e.target.value)}
          spellCheck={false}
        />
      ) : (
        <div
          ref={editorRef}
          className='min-h-[120px] p-2 text-[12px] outline-none'
          contentEditable
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onInput={onInput}
          onPaste={onPaste}
          suppressContentEditableWarning
        />
      )}
    </div>
  );
}
